/**
 * Local API - Axios-like adapter for local storage mode
 * Implements the same interface as axios: get, post, put, delete
 * Returns responses in format: { data: ... }
 * 
 * ENHANCEMENTS:
 * - Export/Import backup (ZIP format)
 * - Hardened CSV export with proper escaping
 */
import JSZip from 'jszip';
import db, {
  firearmsCrud,
  ammunitionCrud,
  accessoriesCrud,
  documentsCrud,
  wishlistCrud,
  settingsCrud
} from './localDb';
import fileStore from './fileStore';

// Current backup schema version
// 1.1: adds ammoLog to data.json (1.0 backups still restore)
const BACKUP_SCHEMA_VERSION = '1.1';

/**
 * Parse URL parameters from query string
 */
function parseParams(url) {
  const params = {};
  const queryStart = url.indexOf('?');
  if (queryStart !== -1) {
    const queryString = url.slice(queryStart + 1);
    const searchParams = new URLSearchParams(queryString);
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
  }
  return params;
}

/**
 * Extract path from URL (remove query string)
 */
function getPath(url) {
  const queryStart = url.indexOf('?');
  return queryStart !== -1 ? url.slice(0, queryStart) : url;
}

/**
 * Process entity with images - saves base64 images as blobs
 * Returns entity with file IDs instead of base64
 */
async function processEntityImages(entity, entityType, entityId) {
  if (!entity.images || entity.images.length === 0) {
    return { ...entity, images: [] };
  }
  
  const fileIds = await fileStore.processImages(entity.images, entityType, entityId);
  return { ...entity, images: fileIds };
}

/**
 * Normalize entity images - ensures images array contains file IDs
 * NO LONGER converts to Base64 - returns file IDs for object URL rendering
 * 
 * IMPORTANT: Components should use EntityImage/useObjectUrl to render images
 */
async function normalizeEntityImages(entity) {
  if (!entity) return entity;
  
  // Ensure images is always an array
  if (!entity.images) {
    return { ...entity, images: [] };
  }
  
  // If images array is empty, return as-is
  if (entity.images.length === 0) {
    return entity;
  }
  
  // Check first image to determine format
  const firstImage = entity.images[0];
  
  // If already file IDs, return as-is (this is the correct format now)
  if (firstImage && firstImage.startsWith('file_')) {
    return entity;
  }
  
  // If it's base64 data (legacy or during form submission), keep as-is
  // The form submission handlers will convert to file IDs
  return entity;
}

/**
 * Normalize multiple entities
 */
async function normalizeEntitiesImages(entities) {
  return Promise.all(entities.map(e => normalizeEntityImages(e)));
}

// ==================== CSV HELPERS (ENHANCEMENT 3) ====================

/**
 * ENHANCEMENT 3: Properly escape a value for CSV
 * Handles: commas, quotes, newlines, arrays, objects, null/undefined
 * @param {any} value - The value to escape
 * @returns {string} - CSV-safe string
 */
function csvEscape(value) {
  // Handle null/undefined
  if (value === null || value === undefined) {
    return '';
  }
  
  // Handle arrays - serialize as JSON
  if (Array.isArray(value)) {
    if (value.length === 0) return '';
    // For simple string arrays, join with semicolons
    if (value.every(v => typeof v === 'string' || typeof v === 'number')) {
      const joined = value.join('; ');
      return csvEscapeString(joined);
    }
    // For complex arrays, use JSON
    return csvEscapeString(JSON.stringify(value));
  }
  
  // Handle objects - serialize as JSON
  if (typeof value === 'object') {
    return csvEscapeString(JSON.stringify(value));
  }
  
  // Handle numbers - keep as-is (no quotes needed unless special chars)
  if (typeof value === 'number') {
    return String(value);
  }
  
  // Handle booleans
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  
  // Handle strings
  return csvEscapeString(String(value));
}

/**
 * Escape a string value for CSV
 * @param {string} str - String to escape
 * @returns {string} - Escaped string
 */
function csvEscapeString(str) {
  // Check if quoting is needed
  const needsQuoting = str.includes(',') || 
                       str.includes('"') || 
                       str.includes('\n') || 
                       str.includes('\r') ||
                       str.includes(';');
  
  if (needsQuoting) {
    // Escape double quotes by doubling them
    const escaped = str.replace(/"/g, '""');
    return `"${escaped}"`;
  }
  
  return str;
}

/**
 * Generate a CSV row from an array of values
 * @param {any[]} values - Array of values
 * @returns {string} - CSV row string
 */
function csvRow(values) {
  return values.map(csvEscape).join(',');
}

// ==================== BACKUP HELPERS (ENHANCEMENT 2) ====================

/**
 * ENHANCEMENT 2: Create a complete backup as ZIP
 * @returns {Promise<Blob>} - ZIP file blob
 */
async function createBackupZip() {
  const zip = new JSZip();
  
  // Gather all data
  const [firearms, ammunition, accessories, documents, wishlist, files, ammoLog] = await Promise.all([
    db.firearms.toArray(),
    db.ammunition.toArray(),
    db.accessories.toArray(),
    db.documents.toArray(),
    db.wishlist.toArray(),
    db.files.toArray(),
    db.ammoLog.toArray()
  ]);
  
  // Get settings
  const settingsArray = await db.settings.toArray();
  const settings = settingsArray.reduce((acc, s) => ({ ...acc, [s.key]: s.value }), {});
  
  // Create manifest
  const manifest = {
    schemaVersion: BACKUP_SCHEMA_VERSION,
    createdAt: new Date().toISOString(),
    appName: 'ArmsRoom',
    counts: {
      firearms: firearms.length,
      ammunition: ammunition.length,
      accessories: accessories.length,
      documents: documents.length,
      wishlist: wishlist.length,
      files: files.length,
      ammoLog: ammoLog.length
    }
  };
  
  // Add manifest
  zip.file('manifest.json', JSON.stringify(manifest, null, 2));
  
  // Add data.json (all structured data without blobs)
  const data = {
    firearms: firearms.map(f => ({ ...f })),
    ammunition: ammunition.map(a => ({ ...a })),
    accessories: accessories.map(a => ({ ...a })),
    documents: documents.map(d => {
      // Remove fileId from documents for export (will be recreated on import)
      const { fileId, ...rest } = d;
      return { ...rest, fileId };
    }),
    wishlist: wishlist.map(w => ({ ...w })),
    ammoLog: ammoLog.map(l => ({ ...l })),
    settings
  };
  
  zip.file('data.json', JSON.stringify(data, null, 2));
  
  // Add files folder with blobs
  const filesFolder = zip.folder('files');
  for (const file of files) {
    if (file.blob) {
      // Convert blob to array buffer for zip
      const arrayBuffer = await fileStore.blobToArrayBuffer(file.blob);
      // Store file metadata alongside
      const fileMeta = {
        id: file.id,
        entityType: file.entityType,
        entityId: file.entityId,
        mimeType: file.mimeType,
        created_at: file.created_at
      };
      filesFolder.file(`${file.id}.meta.json`, JSON.stringify(fileMeta));
      filesFolder.file(`${file.id}.bin`, arrayBuffer);
    }
  }
  
  // Generate ZIP blob
  return await zip.generateAsync({ 
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 }
  });
}

/**
 * ENHANCEMENT 2: Restore from a backup ZIP
 * @param {Blob|File} zipFile - The ZIP file to restore from
 * @param {string} mode - 'replace' (default) or 'merge'
 * @returns {Promise<{success: boolean, message: string, counts: object}>}
 */
async function restoreBackupZip(zipFile, mode = 'replace') {
  try {
    const zip = await JSZip.loadAsync(zipFile);
    
    // Read and validate manifest
    const manifestFile = zip.file('manifest.json');
    if (!manifestFile) {
      throw new Error('Invalid backup: missing manifest.json');
    }
    const manifest = JSON.parse(await manifestFile.async('text'));
    
    // Version check
    if (!manifest.schemaVersion) {
      throw new Error('Invalid backup: missing schema version');
    }
    
    // Read data.json
    const dataFile = zip.file('data.json');
    if (!dataFile) {
      throw new Error('Invalid backup: missing data.json');
    }
    const data = JSON.parse(await dataFile.async('text'));

    // Read every file out of the ZIP BEFORE touching the database, so a
    // corrupt backup fails here and leaves existing data untouched
    const fileEntries = [];
    const filesFolder = zip.folder('files');
    if (filesFolder) {
      const metaFiles = {};

      // First pass: collect all meta files
      filesFolder.forEach((relativePath, file) => {
        if (relativePath.endsWith('.meta.json')) {
          const fileId = relativePath.replace('.meta.json', '');
          metaFiles[fileId] = file;
        }
      });

      // Second pass: process binary files with their metadata
      for (const [fileId, metaFile] of Object.entries(metaFiles)) {
        const binFile = filesFolder.file(`${fileId}.bin`);
        if (binFile) {
          const meta = JSON.parse(await metaFile.async('text'));
          const arrayBuffer = await binFile.async('arraybuffer');
          const blob = new Blob([arrayBuffer], { type: meta.mimeType || 'application/octet-stream' });

          fileEntries.push({
            id: meta.id,
            entityType: meta.entityType,
            entityId: meta.entityId,
            blob: blob,
            mimeType: meta.mimeType,
            created_at: meta.created_at
          });
        }
      }
    }

    // 1.0 backups have no ammoLog - keep the existing log rather than wiping it
    const hasAmmoLog = Array.isArray(data.ammoLog);

    fileStore.revokeAllObjectUrls();

    // Single transaction: if anything fails, IndexedDB rolls back and the
    // previous data is kept. put (not add) so merge mode overwrites matching IDs.
    await db.transaction('rw', [db.firearms, db.ammunition, db.accessories, db.documents, db.wishlist, db.settings, db.ammoLog, db.files], async () => {
      if (mode === 'replace') {
        await Promise.all([
          db.firearms.clear(),
          db.ammunition.clear(),
          db.accessories.clear(),
          db.documents.clear(),
          db.wishlist.clear(),
          db.settings.clear(),
          db.files.clear(),
          hasAmmoLog ? db.ammoLog.clear() : null
        ]);
      }

      if (data.firearms?.length > 0) await db.firearms.bulkPut(data.firearms);
      if (data.ammunition?.length > 0) await db.ammunition.bulkPut(data.ammunition);
      if (data.accessories?.length > 0) await db.accessories.bulkPut(data.accessories);
      if (data.documents?.length > 0) await db.documents.bulkPut(data.documents);
      if (data.wishlist?.length > 0) await db.wishlist.bulkPut(data.wishlist);
      if (hasAmmoLog && data.ammoLog.length > 0) await db.ammoLog.bulkPut(data.ammoLog);
      if (data.settings) {
        await db.settings.bulkPut(Object.entries(data.settings).map(([key, value]) => ({ key, value })));
      }
      if (fileEntries.length > 0) await db.files.bulkPut(fileEntries);
    });

    return {
      success: true,
      message: `Backup restored successfully (${mode} mode)`,
      counts: manifest.counts
    };
    
  } catch (error) {
    console.error('[LocalAPI] Backup restore error:', error);
    return {
      success: false,
      message: 'Restore failed: the backup file is damaged or not an Arms Room backup. Your existing data was not changed.',
      counts: null
    };
  }
}

// ==================== ROUTE HANDLERS ====================

const routes = {
  // -------------------- FIREARMS --------------------
  'GET /firearms': async (params) => {
    const firearms = await firearmsCrud.getAll(params);
    return normalizeEntitiesImages(firearms);
  },
  
  'GET /firearms/:id': async (params, pathParams) => {
    const firearm = await firearmsCrud.getById(pathParams.id);
    if (!firearm) throw { response: { status: 404 }, message: 'Firearm not found' };
    return normalizeEntityImages(firearm);
  },
  
  'POST /firearms': async (params, pathParams, body) => {
    // First create without images to get ID
    const tempFirearm = await firearmsCrud.create({ ...body, images: [] });
    
    // Process images if any
    if (body.images && body.images.length > 0) {
      const fileIds = await fileStore.processImages(body.images, 'firearm', tempFirearm.id);
      const updated = await firearmsCrud.update(tempFirearm.id, { images: fileIds });
      return normalizeEntityImages(updated);
    }
    
    return tempFirearm;
  },
  
  'PUT /firearms/:id': async (params, pathParams, body) => {
    const existing = await firearmsCrud.getById(pathParams.id);
    if (!existing) throw { response: { status: 404 }, message: 'Firearm not found' };
    
    let updateData = { ...body };
    
    // Handle images update
    if (body.images !== undefined) {
      // Process new images (preserves existing file IDs, creates new ones for base64)
      if (body.images.length > 0) {
        const newFileIds = await fileStore.processImages(body.images, 'firearm', pathParams.id);
        
        // Delete only files that are no longer referenced
        const oldFileIds = existing.images || [];
        for (const oldId of oldFileIds) {
          if (!newFileIds.includes(oldId)) {
            await fileStore.deleteFile(oldId);
          }
        }
        
        updateData.images = newFileIds;
      } else {
        // Deleting all images
        await fileStore.deleteFilesForEntity('firearm', pathParams.id);
        updateData.images = [];
      }
    }
    
    const updated = await firearmsCrud.update(pathParams.id, updateData);
    return normalizeEntityImages(updated);
  },
  
  'DELETE /firearms/:id': async (params, pathParams) => {
    await fileStore.deleteFilesForEntity('firearm', pathParams.id);
    return firearmsCrud.delete(pathParams.id);
  },
  
  // -------------------- AMMUNITION --------------------
  'GET /ammunition': async (params) => {
    const ammo = await ammunitionCrud.getAll(params);
    return normalizeEntitiesImages(ammo);
  },
  
  'GET /ammunition/:id': async (params, pathParams) => {
    const ammo = await ammunitionCrud.getById(pathParams.id);
    if (!ammo) throw { response: { status: 404 }, message: 'Ammunition not found' };
    return normalizeEntityImages(ammo);
  },
  
  'POST /ammunition': async (params, pathParams, body) => {
    const tempAmmo = await ammunitionCrud.create({ ...body, images: [] });
    
    if (body.images && body.images.length > 0) {
      const fileIds = await fileStore.processImages(body.images, 'ammunition', tempAmmo.id);
      const updated = await ammunitionCrud.update(tempAmmo.id, { images: fileIds });
      return normalizeEntityImages(updated);
    }
    
    return tempAmmo;
  },
  
  'PUT /ammunition/:id': async (params, pathParams, body) => {
    const existing = await ammunitionCrud.getById(pathParams.id);
    if (!existing) throw { response: { status: 404 }, message: 'Ammunition not found' };
    
    let updateData = { ...body };
    
    if (body.images !== undefined) {
      if (body.images.length > 0) {
        const newFileIds = await fileStore.processImages(body.images, 'ammunition', pathParams.id);
        
        // Delete only files that are no longer referenced
        const oldFileIds = existing.images || [];
        for (const oldId of oldFileIds) {
          if (!newFileIds.includes(oldId)) {
            await fileStore.deleteFile(oldId);
          }
        }
        
        updateData.images = newFileIds;
      } else {
        await fileStore.deleteFilesForEntity('ammunition', pathParams.id);
        updateData.images = [];
      }
    }
    
    const updated = await ammunitionCrud.update(pathParams.id, updateData);
    return normalizeEntityImages(updated);
  },
  
  'POST /ammunition/:id/adjust': async (params, pathParams, body) => {
    const updated = await ammunitionCrud.adjust(pathParams.id, body.amount);
    return normalizeEntityImages(updated);
  },
  
  'DELETE /ammunition/:id': async (params, pathParams) => {
    await fileStore.deleteFilesForEntity('ammunition', pathParams.id);
    return ammunitionCrud.delete(pathParams.id);
  },
  
  // -------------------- ACCESSORIES --------------------
  'GET /accessories': async (params) => {
    const accessories = await accessoriesCrud.getAll(params);
    return normalizeEntitiesImages(accessories);
  },
  
  'GET /accessories/:id': async (params, pathParams) => {
    const accessory = await accessoriesCrud.getById(pathParams.id);
    if (!accessory) throw { response: { status: 404 }, message: 'Accessory not found' };
    return normalizeEntityImages(accessory);
  },
  
  'POST /accessories': async (params, pathParams, body) => {
    const tempAccessory = await accessoriesCrud.create({ ...body, images: [] });
    
    if (body.images && body.images.length > 0) {
      const fileIds = await fileStore.processImages(body.images, 'accessory', tempAccessory.id);
      const updated = await accessoriesCrud.update(tempAccessory.id, { images: fileIds });
      return normalizeEntityImages(updated);
    }
    
    return tempAccessory;
  },
  
  'PUT /accessories/:id': async (params, pathParams, body) => {
    const existing = await accessoriesCrud.getById(pathParams.id);
    if (!existing) throw { response: { status: 404 }, message: 'Accessory not found' };
    
    let updateData = { ...body };
    
    if (body.images !== undefined) {
      if (body.images.length > 0) {
        const newFileIds = await fileStore.processImages(body.images, 'accessory', pathParams.id);
        
        // Delete only files that are no longer referenced
        const oldFileIds = existing.images || [];
        for (const oldId of oldFileIds) {
          if (!newFileIds.includes(oldId)) {
            await fileStore.deleteFile(oldId);
          }
        }
        
        updateData.images = newFileIds;
      } else {
        await fileStore.deleteFilesForEntity('accessory', pathParams.id);
        updateData.images = [];
      }
    }
    
    const updated = await accessoriesCrud.update(pathParams.id, updateData);
    return normalizeEntityImages(updated);
  },
  
  'DELETE /accessories/:id': async (params, pathParams) => {
    await fileStore.deleteFilesForEntity('accessory', pathParams.id);
    return accessoriesCrud.delete(pathParams.id);
  },
  
  // -------------------- DOCUMENTS --------------------
  'GET /documents': async (params) => {
    return documentsCrud.getAll(params);
  },
  
  'GET /documents/:id': async (params, pathParams) => {
    const doc = await documentsCrud.getById(pathParams.id);
    if (!doc) throw { response: { status: 404 }, message: 'Document not found' };
    return doc;
  },
  
  'POST /documents': async (params, pathParams, body) => {
    return documentsCrud.create(body);
  },
  
  'PUT /documents/:id': async (params, pathParams, body) => {
    return documentsCrud.update(pathParams.id, body);
  },
  
  'DELETE /documents/:id': async (params, pathParams) => {
    return documentsCrud.delete(pathParams.id);
  },
  
  // -------------------- WISHLIST --------------------
  'GET /wishlist': async (params) => {
    const items = await wishlistCrud.getAll(params);
    return normalizeEntitiesImages(items);
  },
  
  'GET /wishlist/:id': async (params, pathParams) => {
    const item = await wishlistCrud.getById(pathParams.id);
    if (!item) throw { response: { status: 404 }, message: 'Wishlist item not found' };
    return normalizeEntityImages(item);
  },
  
  'POST /wishlist': async (params, pathParams, body) => {
    const tempItem = await wishlistCrud.create({ ...body, images: [] });
    
    if (body.images && body.images.length > 0) {
      const fileIds = await fileStore.processImages(body.images, 'wishlist', tempItem.id);
      const updated = await wishlistCrud.update(tempItem.id, { images: fileIds });
      return normalizeEntityImages(updated);
    }
    
    return tempItem;
  },
  
  'PUT /wishlist/:id': async (params, pathParams, body) => {
    const existing = await wishlistCrud.getById(pathParams.id);
    if (!existing) throw { response: { status: 404 }, message: 'Wishlist item not found' };
    
    let updateData = { ...body };
    
    if (body.images !== undefined) {
      if (body.images.length > 0) {
        const newFileIds = await fileStore.processImages(body.images, 'wishlist', pathParams.id);
        
        // Delete only files that are no longer referenced
        const oldFileIds = existing.images || [];
        for (const oldId of oldFileIds) {
          if (!newFileIds.includes(oldId)) {
            await fileStore.deleteFile(oldId);
          }
        }
        
        updateData.images = newFileIds;
      } else {
        await fileStore.deleteFilesForEntity('wishlist', pathParams.id);
        updateData.images = [];
      }
    }
    
    const updated = await wishlistCrud.update(pathParams.id, updateData);
    return normalizeEntityImages(updated);
  },
  
  'DELETE /wishlist/:id': async (params, pathParams) => {
    await fileStore.deleteFilesForEntity('wishlist', pathParams.id);
    return wishlistCrud.delete(pathParams.id);
  },
  
  // -------------------- DASHBOARD --------------------
  'GET /dashboard/stats': async () => {
    const [firearms, ammoRounds, accessories, documents, wishlist] = await Promise.all([
      firearmsCrud.count(),
      ammunitionCrud.getTotalRounds(),
      accessoriesCrud.count(),
      documentsCrud.count(),
      wishlistCrud.count()
    ]);
    
    return {
      firearms,
      ammunition_rounds: ammoRounds,
      accessories,
      documents,
      wishlist
    };
  },
  
  'GET /dashboard/recent': async () => {
    const [firearms, ammunition, accessories] = await Promise.all([
      firearmsCrud.getRecent(3).then(normalizeEntitiesImages),
      ammunitionCrud.getRecent(3).then(normalizeEntitiesImages),
      accessoriesCrud.getRecent(3).then(normalizeEntitiesImages)
    ]);
    
    return { firearms, ammunition, accessories };
  },
  
  // Collection value calculation
  'GET /dashboard/collection-value': async () => {
    const [allFirearms, allAmmunition, allAccessories] = await Promise.all([
      firearmsCrud.getAll(),
      ammunitionCrud.getAll(),
      accessoriesCrud.getAll()
    ]);
    
    // Sum firearms current_value
    const firearmsValue = allFirearms.reduce((sum, f) => {
      return sum + (parseFloat(f.current_value) || 0);
    }, 0);
    
    // Sum accessories purchase_price
    const accessoriesValue = allAccessories.reduce((sum, a) => {
      return sum + (parseFloat(a.purchase_price) || 0);
    }, 0);
    
    // Sum ammunition value (costPerRound * rounds)
    const ammunitionValue = allAmmunition.reduce((sum, a) => {
      const costPaid = parseFloat(a.totalCostPaid) || 0;
      const roundsPurchased = parseInt(a.roundsPurchased) || 0;
      const currentRounds = parseInt(a.rounds) || 0;
      
      // If we have cost data, calculate based on cost per round * current rounds
      if (costPaid > 0 && roundsPurchased > 0) {
        const costPerRound = costPaid / roundsPurchased;
        return sum + (costPerRound * currentRounds);
      }
      return sum;
    }, 0);
    
    const totalValue = firearmsValue + accessoriesValue + ammunitionValue;
    
    return {
      total: totalValue,
      breakdown: {
        firearms: firearmsValue,
        ammunition: ammunitionValue,
        accessories: accessoriesValue
      },
      counts: {
        firearms: allFirearms.length,
        ammunition: allAmmunition.length,
        accessories: allAccessories.length
      }
    };
  },
  
  // -------------------- SETTINGS --------------------
  'GET /settings': async () => {
    const settings = await settingsCrud.getAll();
    return {
      user_id: 'local_user',
      theme: settings.theme || 'dark',
      ...settings
    };
  },
  
  'PUT /settings': async (params, pathParams, body) => {
    if (body.theme) await settingsCrud.set('theme', body.theme);
    return {
      user_id: 'local_user',
      theme: body.theme || 'dark',
      updated_at: new Date().toISOString()
    };
  },
  
  // -------------------- AUTH (no-op for local mode) --------------------
  'POST /auth/session': async () => {
    return {
      user_id: 'local_user',
      email: 'local@armsroom.local',
      name: 'Local User',
      picture: null
    };
  },
  
  'GET /auth/me': async () => {
    return {
      user_id: 'local_user',
      email: 'local@armsroom.local',
      name: 'Local User',
      picture: null
    };
  },
  
  'POST /auth/logout': async () => {
    return { message: 'Logged out successfully' };
  },
  
  // -------------------- EXPORT CSV (Standards-Compliant Flat Format) --------------------
  'GET /export/csv': async () => {
    const [firearms, ammunition, accessories] = await Promise.all([
      firearmsCrud.getAll(),
      ammunitionCrud.getAll(),
      accessoriesCrud.getAll()
    ]);
    
    // Canonical CSV schema - single flat table
    const headers = [
      'item_type', 'id', 'manufacturer', 'model', 'category', 'caliber',
      'quantity', 'unit_cost', 'total_value', 'notes', 'created_at', 'updated_at'
    ];
    
    // Start with header row
    let csv = csvRow(headers) + '\n';
    
    // Normalize firearms
    firearms.forEach(f => {
      const currentValue = parseFloat(f.current_value) || 0;
      csv += csvRow([
        'firearm',
        f.id || '',
        f.manufacturer || '',
        f.model || '',
        f.type || '',  // Pistol/Rifle/Shotgun/Other
        f.caliber || '',
        1,  // Firearms always quantity 1
        currentValue > 0 ? currentValue : '',
        currentValue > 0 ? currentValue : '',
        f.notes || '',
        f.created_at || '',
        f.updated_at || ''
      ]) + '\n';
    });
    
    // Normalize ammunition
    ammunition.forEach(a => {
      const rounds = parseInt(a.rounds) || 0;
      const totalCostPaid = parseFloat(a.totalCostPaid) || 0;
      const roundsPurchased = parseInt(a.roundsPurchased) || 0;
      
      // Calculate cost per round and total value
      let costPerRound = '';
      let totalValue = '';
      if (totalCostPaid > 0 && roundsPurchased > 0) {
        costPerRound = (totalCostPaid / roundsPurchased).toFixed(4);
        totalValue = (parseFloat(costPerRound) * rounds).toFixed(2);
      }
      
      csv += csvRow([
        'ammunition',
        a.id || '',
        a.brand || '',  // Brand maps to manufacturer
        a.type || '',   // FMJ/JHP maps to model
        a.type || '',   // Also use type for category
        a.caliber || '',
        rounds,
        costPerRound,
        totalValue,
        a.notes || '',
        a.created_at || '',
        a.updated_at || ''
      ]) + '\n';
    });
    
    // Normalize accessories
    accessories.forEach(acc => {
      const purchasePrice = parseFloat(acc.purchase_price) || 0;
      csv += csvRow([
        'accessory',
        acc.id || '',
        acc.brand || '',   // Brand maps to manufacturer
        acc.model || '',
        acc.type || '',    // Optic/Mag/Holster/etc
        '',                // Caliber N/A for accessories
        1,                 // Accessories are individual items
        purchasePrice > 0 ? purchasePrice : '',
        purchasePrice > 0 ? purchasePrice : '',
        acc.notes || '',
        acc.created_at || '',
        acc.updated_at || ''
      ]) + '\n';
    });
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    return blob;
  },
  
  // -------------------- BACKUP/RESTORE (ENHANCEMENT 2) --------------------
  'GET /export/backup': async () => {
    const zipBlob = await createBackupZip();
    return zipBlob;
  },
  
  'POST /import/backup': async (params, pathParams, body) => {
    // body should contain { file: Blob/File, mode: 'replace'|'merge' }
    const { file, mode = 'replace' } = body;
    if (!file) {
      throw { response: { status: 400 }, message: 'No backup file provided' };
    }
    return await restoreBackupZip(file, mode);
  },
  
  // -------------------- HEALTH --------------------
  'GET /': async () => {
    return { message: 'Arms Room Local API', status: 'healthy' };
  },
  
  'GET /health': async () => {
    return { status: 'healthy', timestamp: new Date().toISOString(), mode: 'local' };
  }
};

/**
 * Match a URL pattern with path parameters
 * e.g., '/firearms/:id' matches '/firearms/123' and extracts { id: '123' }
 */
function matchRoute(method, url) {
  const path = getPath(url);
  const queryParams = parseParams(url);
  
  // Try exact match first
  const exactKey = `${method} ${path}`;
  if (routes[exactKey]) {
    return { handler: routes[exactKey], params: queryParams, pathParams: {} };
  }
  
  // Try pattern matching
  for (const [routeKey, handler] of Object.entries(routes)) {
    const [routeMethod, routePattern] = routeKey.split(' ');
    if (routeMethod !== method) continue;
    
    const routeParts = routePattern.split('/');
    const pathParts = path.split('/');
    
    if (routeParts.length !== pathParts.length) continue;
    
    const pathParams = {};
    let match = true;
    
    for (let i = 0; i < routeParts.length; i++) {
      if (routeParts[i].startsWith(':')) {
        pathParams[routeParts[i].slice(1)] = pathParts[i];
      } else if (routeParts[i] !== pathParts[i]) {
        match = false;
        break;
      }
    }
    
    if (match) {
      return { handler, params: queryParams, pathParams };
    }
  }
  
  return null;
}

/**
 * Create axios-like response
 */
function createResponse(data, status = 200) {
  return {
    data,
    status,
    statusText: status === 200 ? 'OK' : 'Error',
    headers: {},
    config: {}
  };
}

/**
 * The local API adapter - mimics axios interface
 */
const localApi = {
  async get(url, config = {}) {
    const route = matchRoute('GET', url);
    if (!route) {
      console.warn(`[LocalAPI] No route found for GET ${url}`);
      throw { response: { status: 404 }, message: `Route not found: GET ${url}` };
    }
    
    try {
      const result = await route.handler(route.params, route.pathParams);
      
      // Handle blob responses (for CSV/backup export)
      if (result instanceof Blob) {
        return createResponse(result);
      }
      
      return createResponse(result);
    } catch (error) {
      console.error(`[LocalAPI] Error in GET ${url}:`, error);
      throw error;
    }
  },
  
  async post(url, data = {}, config = {}) {
    const route = matchRoute('POST', url);
    if (!route) {
      console.warn(`[LocalAPI] No route found for POST ${url}`);
      throw { response: { status: 404 }, message: `Route not found: POST ${url}` };
    }
    
    try {
      const result = await route.handler(route.params, route.pathParams, data);
      return createResponse(result);
    } catch (error) {
      console.error(`[LocalAPI] Error in POST ${url}:`, error);
      throw error;
    }
  },
  
  async put(url, data = {}, config = {}) {
    const route = matchRoute('PUT', url);
    if (!route) {
      console.warn(`[LocalAPI] No route found for PUT ${url}`);
      throw { response: { status: 404 }, message: `Route not found: PUT ${url}` };
    }
    
    try {
      const result = await route.handler(route.params, route.pathParams, data);
      return createResponse(result);
    } catch (error) {
      console.error(`[LocalAPI] Error in PUT ${url}:`, error);
      throw error;
    }
  },
  
  async delete(url, config = {}) {
    const route = matchRoute('DELETE', url);
    if (!route) {
      console.warn(`[LocalAPI] No route found for DELETE ${url}`);
      throw { response: { status: 404 }, message: `Route not found: DELETE ${url}` };
    }
    
    try {
      const result = await route.handler(route.params, route.pathParams);
      return createResponse(result);
    } catch (error) {
      console.error(`[LocalAPI] Error in DELETE ${url}:`, error);
      throw error;
    }
  },
  
  // Interceptors (no-op for compatibility)
  interceptors: {
    request: {
      use: (onFulfilled, onRejected) => {},
      eject: () => {}
    },
    response: {
      use: (onFulfilled, onRejected) => {},
      eject: () => {}
    }
  }
};

export default localApi;
